import { Injectable } from '@angular/core';
import { ICropImageSelection } from '@fi-app/utilities/crop-image/crop-image';
import { MatDialogRef } from '@angular/material/dialog';
import { OperationOverlaySmallDialogComponent } from '@fi-shared/operation-overlay-small/operation-overlay-small-dialog/operation-overlay-small-dialog.component';

export interface IEditProfileCropImageResult {
    result: MatDialogRef<
        OperationOverlaySmallDialogComponent,
        {
            selection: ICropImageSelection;
            data: string;
        }
    >;
    closeDialog: () => void;
}

@Injectable()
export class CropImageService {
    private maxSideLength = 300;

    constructor() {}

    getSquareImage(src: string) {
        return new Promise<{
            // const promise = new Promise<{
            data: string;
            selection: ICropImageSelection;
        }>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                if (width && height) {
                    const fCanvas: HTMLCanvasElement = document.createElement('canvas');
                    fCanvas.width = width;
                    fCanvas.height = height;
                    const fCtx = fCanvas.getContext('2d');
                    fCtx.drawImage(img, 0, 0);
                    const tCanvas: HTMLCanvasElement = document.createElement('canvas');
                    const tCtx = tCanvas.getContext('2d');
                    const imgSize = width < height ? width : height;
                    tCanvas.width = tCanvas.height = imgSize;
                    const x = width > imgSize ? (width - imgSize) / 2 : 0;
                    const y = height > imgSize ? (height - imgSize) / 2 : 0;
                    tCtx.putImageData(fCtx.getImageData(x, y, imgSize, imgSize), 0, 0);

                    // calculate selection
                    const ratio = width < height ? width / height : height / width;
                    const selectionSize = this.maxSideLength * ratio;
                    const selection = {
                        x: (this.maxSideLength - selectionSize) / 2,
                        y: (this.maxSideLength - selectionSize) / 2,
                        width: selectionSize,
                        height: selectionSize,
                    };

                    resolve({
                        data: tCanvas.toDataURL('image/jpeg'),
                        selection: selection,
                    });
                } else {
                    resolve(null);
                }
            };
            img.onerror = () => {
                reject();
            };
            img.src = src;
        });
    }

    fixOrientation(src: string) {
        return new Promise<string>((resolve, reject) => {
            // check exif
            const base64 = src.replace(/^data\:([^\;]+)\;base64,/gim, '');
            const binary_string = window.atob(base64);
            const len = binary_string.length;
            const ab = new ArrayBuffer(len);
            const bytes = new Uint8Array(ab);
            for (let i = 0; i < len; i++) {
                bytes[i] = binary_string.charCodeAt(i);
            }
            let orientation: number;
            const view = new DataView(ab);
            if (view.getUint16(0, false) != 0xffd8) {
                orientation = -2;
            } else {
                let length = view.byteLength,
                    offset = 2;
                while (offset < length && !orientation) {
                    const marker = view.getUint16(offset, false);
                    offset += 2;
                    if (marker == 0xffe1) {
                        if (view.getUint32((offset += 2), false) != 0x45786966) {
                            orientation = -1;
                        } else {
                            const little = view.getUint16((offset += 6), false) == 0x4949;
                            offset += view.getUint32(offset + 4, little);
                            const tags = view.getUint16(offset, little);
                            offset += 2;
                            for (let i = 0; i < tags && !orientation; i++) {
                                if (view.getUint16(offset + i * 12, little) == 0x0112) {
                                    orientation = view.getUint16(offset + i * 12 + 8, little);
                                }
                            }
                        }
                    } else {
                        if ((marker & 0xff00) != 0xff00) break;
                        else offset += view.getUint16(offset, false);
                    }
                }
                if (!orientation) orientation = -1;
            }
            if (orientation > 0) {
                const img = new Image();
                img.crossOrigin = 'Anonymous';

                img.onload = function () {
                    const width = img.width,
                        height = img.height,
                        canvas: HTMLCanvasElement = document.createElement('canvas'),
                        ctx = canvas.getContext('2d');

                    // set proper canvas dimensions before transform & export
                    if ([5, 6, 7, 8].indexOf(orientation) > -1) {
                        canvas.width = height;
                        canvas.height = width;
                    } else {
                        canvas.width = width;
                        canvas.height = height;
                    }

                    // transform context before drawing image
                    switch (orientation) {
                        case 2:
                            ctx.transform(-1, 0, 0, 1, width, 0);
                            break;
                        case 3:
                            ctx.transform(-1, 0, 0, -1, width, height);
                            break;
                        case 4:
                            ctx.transform(1, 0, 0, -1, 0, height);
                            break;
                        case 5:
                            ctx.transform(0, 1, 1, 0, 0, 0);
                            break;
                        case 6:
                            ctx.transform(0, 1, -1, 0, height, 0);
                            break;
                        case 7:
                            ctx.transform(0, -1, -1, 0, height, width);
                            break;
                        case 8:
                            ctx.transform(0, -1, 1, 0, 0, width);
                            break;
                        default:
                            ctx.transform(1, 0, 0, 1, 0, 0);
                    }

                    // draw image
                    ctx.drawImage(img, 0, 0);

                    // export base64
                    resolve(canvas.toDataURL());
                };

                img.src = src;
            } else {
                resolve(src);
            }
        });
    }

    base64toUint8Array(dataURI): Uint8Array {
        const byteString =
            dataURI.split(',')[0].indexOf('base64') >= 0
                ? atob(dataURI.split(',')[1])
                : decodeURIComponent(dataURI.split(',')[1]);

        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return ia;
    }
}
